/**
 * Small inline SVG icon set (stroke icons, 16 px grid, `currentColor`). Inline so the CSP needs no
 * extra host and icons inherit text colour. Decorative by default (aria-hidden); give the parent
 * control an accessible name.
 */
import { memo } from "react";

const PATHS = {
  lock: "M4.5 7V5a3.5 3.5 0 0 1 7 0v2M3.5 7h9v6.5h-9z",
  unlock: "M4.5 7V5a3.5 3.5 0 0 1 6.8-1.2M3.5 7h9v6.5h-9z",
  gear: "M8 5.5a2.5 2.5 0 1 0 0 5 2.5 2.5 0 0 0 0-5zM8 1.5v1.8M8 12.7v1.8M1.5 8h1.8M12.7 8h1.8M3.4 3.4l1.3 1.3M11.3 11.3l1.3 1.3M3.4 12.6l1.3-1.3M11.3 4.7l1.3-1.3",
  eye: "M1.5 8S4 3.5 8 3.5 14.5 8 14.5 8 12 12.5 8 12.5 1.5 8 1.5 8zM8 6a2 2 0 1 0 0 4 2 2 0 0 0 0-4z",
  eyeOff:
    "M2 2l12 12M6.6 3.7A6.8 6.8 0 0 1 8 3.5C12 3.5 14.5 8 14.5 8a11 11 0 0 1-1.9 2.4M10.1 10.1A2 2 0 0 1 6 8.1M4.3 5A11 11 0 0 0 1.5 8S4 12.5 8 12.5c1 0 1.9-.2 2.7-.6",
  chevronDown: "M4 6l4 4 4-4",
  plus: "M8 3v10M3 8h10",
  minus: "M3 8h10",
  search: "M7 2.5a4.5 4.5 0 1 0 0 9 4.5 4.5 0 0 0 0-9zM10.3 10.3L13.5 13.5",
  bell: "M4 11V7a4 4 0 0 1 8 0v4l1 1.5H3zM6.5 14h3",
  close: "M4 4l8 8M12 4l-8 8",
  home: "M2.5 7.5L8 3l5.5 4.5V13.5h-3.5v-3.5h-4v3.5H2.5z",
  chart: "M2.5 13.5h11M4.5 11V7M8 11V4M11.5 11V8.5",
  markets: "M2.5 12l3.5-4 3 2.5 4.5-6M10.5 4.5h3v3",
  positions: "M3 3.5h10v9H3zM3 7h10M6.5 7v5.5",
  user: "M8 8a2.75 2.75 0 1 0 0-5.5A2.75 2.75 0 0 0 8 8zM2.5 14a5.5 5.5 0 0 1 11 0",
  command:
    "M5.5 5.5h5v5h-5zM5.5 5.5V4a1.5 1.5 0 1 0-1.5 1.5zM10.5 5.5V4A1.5 1.5 0 1 1 12 5.5zM5.5 10.5V12A1.5 1.5 0 1 1 4 10.5zM10.5 10.5V12a1.5 1.5 0 1 0 1.5-1.5z",
  warning: "M8 2l6.5 11.5h-13zM8 6.5v3M8 11.5v.01",
  center: "M8 1.5v3M8 11.5v3M1.5 8h3M11.5 8h3M8 6a2 2 0 1 0 0 4 2 2 0 0 0 0-4z",
  bolt: "M9 1.5L3.5 9h4L7 14.5 12.5 7h-4z",
  star: "M8 1.8l1.9 3.9 4.3.6-3.1 3 .7 4.3L8 11.6l-3.8 2 .7-4.3-3.1-3 4.3-.6z",
  starFilled: "M8 1.8l1.9 3.9 4.3.6-3.1 3 .7 4.3L8 11.6l-3.8 2 .7-4.3-3.1-3 4.3-.6z",
  // Workspace chrome and module icons (add-module menu, panel header actions).
  ladder: "M4 2.5v11M12 2.5v11M4 5h8M4 8h8M4 11h8",
  orderPad: "M3 2.5h10v11H3zM5.5 5.5h5M5.5 8h5M5.5 10.5h2.5",
  tape: "M2.5 4h11M2.5 7h11M2.5 10h11M2.5 13h7",
  list: "M5.5 4h8M5.5 8h8M5.5 12h8M2.5 4h.01M2.5 8h.01M2.5 12h.01",
  book: "M2.5 3.5h5v9h-5zM8.5 5.5h5v7h-5z",
  accounts: "M2.5 4.5h11v8h-11zM2.5 7h11M5 10h2",
  orders: "M3.5 2.5h9v11h-9zM6 5.5h4M6 8h4M6 10.5h2",
  fills: "M3 8.5l3 3 7-7",
  journal: "M4 2.5h8.5v11H4zM4 2.5a1.5 1.5 0 0 0-1.5 1.5v8A1.5 1.5 0 0 0 4 13.5M6.5 5.5h4M6.5 8h4",
  shield: "M8 1.8l5 2v4c0 3-2.2 5.3-5 6.4-2.8-1.1-5-3.4-5-6.4v-4z",
  copy: "M5.5 5.5h8v8h-8zM10.5 5.5v-3h-8v8h3",
  notes: "M3 2.5h7l3 3v8H3zM10 2.5v3h3",
  replay: "M3 8a5 5 0 1 0 1.5-3.6M3 2.5v2.5h2.5",
  code: "M5.5 4.5L2 8l3.5 3.5M10.5 4.5L14 8l-3.5 3.5",
  popout: "M9 2.5h4.5V7M13.5 2.5L8 8M11.5 9.5v4h-9v-9h4",
  float: "M2.5 2.5h8v6h-8zM5.5 11.5h8v-6",
  maximize: "M2.5 6V2.5H6M10 2.5h3.5V6M13.5 10v3.5H10M6 13.5H2.5V10",
  restore: "M6 2.5V6H2.5M10 2.5V6h3.5M13.5 10H10v3.5M6 13.5V10H2.5",
  dock: "M2.5 2.5h11v11h-11zM2.5 9.5h11",
  undo: "M5.5 3.5L2.5 6.5l3 3M2.5 6.5H10a3.5 3.5 0 0 1 0 7H7",
  layout: "M2.5 2.5h11v11h-11zM2.5 6h11M7 6v7.5",
  more: "M4 8h.01M8 8h.01M12 8h.01",
  pencil: "M10.5 2.5l3 3-8 8H2.5v-3z",
  trash: "M3 4.5h10M6.5 4.5V3h3v1.5M4.5 4.5l.7 9h5.6l.7-9",
  // Chart overlays and the drawing-tools rail (Glacis references, mockups 01/02).
  layers: "M8 2.5l5.5 3L8 8.5 2.5 5.5zM2.5 8L8 11l5.5-3M2.5 10.5L8 13.5l5.5-3",
  // Compare symbols (two price lines) and chart templates (a framed chart with a bookmark).
  compare: "M2 11l3.5-4 3 2.5L13.5 4M2 13.5l3.5-3 3 1.5 5-4",
  template: "M2.5 2.5h11v11h-11zM5 10.5l2-2.5 2 1.5 2-3M10 2.5v4l1.25-1 1.25 1v-4",
  cursor: "M4 2.5l8 5.5-3.5.8L10.5 13l-1.6.8-2-4.2L4 12z",
  crosshair: "M8 2v12M2 8h12",
  trend: "M3 13L13 3M3 13h.01M13 3h.01",
  hline: "M2 8h12M5 8h.01",
  rect: "M3 4.5h10v7H3z",
  text: "M3.5 3.5h9M8 3.5v9",
  ruler: "M2.5 10.5l8-8 3 3-8 8zM5 8l1.5 1.5M7 6l1.5 1.5M9 4l1.5 1.5",
  magnet: "M4 3v5a4 4 0 0 0 8 0V3M4 5.5h2.5M9.5 5.5H12",
  screener: "M2.5 3.5h11l-4.5 5v4l-2 1v-5z",
  // Tradovate chart quick buttons (owner 2026-09-29, reference 10).
  zoomIn: "M7 2.5a4.5 4.5 0 1 0 0 9 4.5 4.5 0 0 0 0-9zM10.3 10.3L13.5 13.5M5 7h4M7 5v4",
  zoomOut: "M7 2.5a4.5 4.5 0 1 0 0 9 4.5 4.5 0 0 0 0-9zM10.3 10.3L13.5 13.5M5 7h4",
  boxZoom: "M2.5 5V2.5H5M11 2.5h2.5V5M13.5 11v2.5H11M5 13.5H2.5V11M6.5 2.5h3M6.5 13.5h3M2.5 6.5v3M13.5 6.5v3",
  candles: "M5 2v2.5M5 11.5V14M3.5 4.5h3v7h-3zM11 3v3M11 11v2M9.5 6h3v5h-3z",
  camera: "M2.5 5h2.5l1-1.5h4L11 5h2.5v7.5h-11zM8 6.5a2.25 2.25 0 1 0 0 4.5 2.25 2.25 0 0 0 0-4.5z",
  arrowLeft: "M13 8H3M7 4L3 8l4 4",
  dataBox: "M3 2.5h10v11H3zM5.5 5.5h5M5.5 8h5M5.5 10.5h5",
  pin: "M6 2.5h4M7 2.5v4L4.5 9h7L9 6.5v-4M8 9v4.5",
} as const;

/** Icons drawn filled rather than stroked. */
const FILLED: ReadonlySet<string> = new Set(["starFilled"]);

export type IconName = keyof typeof PATHS;

export const Icon = memo(function Icon({
  name,
  size = 16,
  className,
}: {
  name: IconName;
  size?: number;
  className?: string;
}): React.JSX.Element {
  return (
    <svg
      className={className ? `icon ${className}` : "icon"}
      width={size}
      height={size}
      viewBox="0 0 16 16"
      fill={FILLED.has(name) ? "currentColor" : "none"}
      stroke="currentColor"
      strokeWidth="1.4"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      <path d={PATHS[name]} />
    </svg>
  );
});
